import React from 'react';
import { ChevronDown, ChevronRight, Folder } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DocumentContext } from '@/pages/KnowledgeBase/KnowledgeBasePage';
import { folderContext, folderNodeKey, type FolderNode } from '@/lib/knowledgeFolders';

interface KnowledgeFolderTreeProps {
  nodes: FolderNode[];
  /** Root context of the domain, use case or solution owning the folders. */
  context: DocumentContext;
  selectedContext: DocumentContext | null;
  expandedFolders: Set<string>;
  onToggle: (key: string) => void;
  onSelect: (context: DocumentContext) => void;
}

export const KnowledgeFolderTree: React.FC<KnowledgeFolderTreeProps> = ({
  nodes,
  context,
  selectedContext,
  expandedFolders,
  onToggle,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {nodes.map((node) => {
        const key = folderNodeKey(context, node.path);
        const isExpanded = expandedFolders.has(key);
        const isSelected =
          selectedContext?.type === context.type &&
          selectedContext.id === context.id &&
          selectedContext.folderPath === node.path;

        return (
          <div key={node.path} className="ml-5 space-y-1">
            <div className="flex items-center gap-1">
              {node.children.length > 0 ? (
                <button
                  onClick={() => onToggle(key)}
                  className="p-1 hover:bg-accent rounded"
                  aria-label={t('knowledgeBasePage.actions.toggleFolder', { name: node.name })}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              ) : (
                <span className="w-6 flex-shrink-0" />
              )}
              <div
                onClick={() => onSelect(folderContext(context, node.path))}
                className={`
                  flex-1 flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors
                  hover:bg-accent
                  ${isSelected ? 'bg-primary/10 text-primary font-medium' : ''}
                `}
              >
                <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <span
                  className="text-sm flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-[160px]"
                  title={node.path}
                >
                  {node.name}
                </span>
                <span
                  className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex-shrink-0"
                  title={t('documents.explorer.folderDocuments', { count: node.documentCount })}
                >
                  {node.documentCount}
                </span>
              </div>
            </div>

            {isExpanded && (
              <KnowledgeFolderTree
                nodes={node.children}
                context={context}
                selectedContext={selectedContext}
                expandedFolders={expandedFolders}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            )}
          </div>
        );
      })}
    </>
  );
};
